import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CATEGORIES, FEES, inr, EVENT } from "@/data/content";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/components/ProtectedRoute";
import { toast } from "sonner";
import { Loader2, Minus, Plus, CheckCircle2, PartyPopper } from "lucide-react";
import { PhonePeQr } from "@/components/landing/PhonePeQr";
import { PaymentProofForm } from "@/components/landing/PaymentProofForm";

const empty = {
  full_name: "",
  email: "",
  phone: "",
  city: "",
  organization: "",
  category: "",
  is_jfc: false,
  enroller_name: "",
  accompanying_members: 0,
  stay_tour: false,
  stay_tour_persons: 0,
  av_required: false,
  notes: "",
};

const Counter = ({ label, value, onChange, testid }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-zinc-300">{label}</span>
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onChange(Math.max(0, value - 1))}
        className="w-8 h-8 rounded-lg gold-border flex items-center justify-center text-amber-300 hover:bg-amber-500/10"
        data-testid={`${testid}-minus`}
      >
        <Minus className="w-4 h-4" />
      </button>
      <span className="font-mono-lux w-6 text-center text-amber-100" data-testid={testid}>
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        className="w-8 h-8 rounded-lg gold-border flex items-center justify-center text-amber-300 hover:bg-amber-500/10"
        data-testid={`${testid}-plus`}
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  </div>
);

export const RegistrationDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fees = useMemo(() => {
    const registration_fee = form.is_jfc ? 0 : FEES.REGULAR;
    const extra = Math.max(0, form.accompanying_members - 1);
    const extra_member_fee = extra * FEES.EXTRA_MEMBER;
    const stay_tour_fee = form.stay_tour ? form.stay_tour_persons * FEES.STAY_TOUR : 0;
    return {
      registration_fee,
      extra_member_fee,
      stay_tour_fee,
      total: registration_fee + extra_member_fee + stay_tour_fee,
    };
  }, [form]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.category) {
      toast.error("Please fill name, email, phone and category.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/registrations", form);
      setDone(true);
      window.fbq?.("track", "CompleteRegistration", {
        value: fees.total, currency: "USD", content_name: form.category, status: "submitted",
      });
      toast.success("Nomination submitted successfully!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setForm(empty);
      setDone(false);
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#121216] border-amber-500/25 text-zinc-100">
        {done ? (
          <div className="py-6 text-center" data-testid="registration-success">
            <PartyPopper className="w-14 h-14 text-amber-400 mx-auto mb-4" />
            <h3 className="font-display text-2xl gold-text font-700">Nomination Received!</h3>
            <p className="text-zinc-400 mt-3 max-w-md mx-auto">
              Thank you for registering for the South Icon Awards 2026. A confirmation email with
              your fee breakdown{fees.total > 0 ? " and payment QR" : ""} has been sent to{" "}
              <span className="text-amber-100" data-testid="registration-success-email">{form.email}</span>.
              Our team will contact you shortly to confirm your participation.
            </p>
            {fees.total > 0 ? (
              <div className="mt-7 p-6 rounded-2xl bg-[#18181F] gold-border" data-testid="payment-instructions">
                <div className="font-mono-lux text-xs tracking-[0.2em] text-amber-400 uppercase mb-4">
                  Complete Your Payment
                </div>
                <PhonePeQr amount={fees.total} />
                <div className="mt-6 pt-6 border-t border-amber-500/15">
                  <p className="text-sm text-zinc-300 mb-4">
                    Paid already? Share your transaction ID or screenshot so we can verify faster.
                  </p>
                  <PaymentProofForm email={form.email} phone={form.phone} lockIdentity />
                </div>
              </div>
            ) : (
              <p className="text-sm text-amber-200/80 mt-5" data-testid="payment-waived">
                No registration fee is applicable for JFC nominees.
              </p>
            )}
            <button
              onClick={close}
              className="mt-7 gold-gradient-bg text-black font-600 px-8 py-3 rounded-full"
              data-testid="registration-success-close"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl gold-text">
                Awardee Nomination & Registration
              </DialogTitle>
              <DialogDescription className="text-sm text-zinc-400">
                {EVENT.date} · {EVENT.venue}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-5 mt-2" data-testid="nomination-registration-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Full Name *</Label>
                  <Input
                    value={form.full_name}
                    onChange={(e) => set("full_name", e.target.value)}
                    className="mt-1.5 bg-[#18181F] border-amber-500/20"
                    placeholder="Your full name"
                    data-testid="registration-full-name-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Phone *</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    className="mt-1.5 bg-[#18181F] border-amber-500/20"
                    placeholder="+91 ..."
                    data-testid="registration-phone-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className="mt-1.5 bg-[#18181F] border-amber-500/20"
                    placeholder="you@email.com"
                    data-testid="registration-email-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">City</Label>
                  <Input
                    value={form.city}
                    onChange={(e) => set("city", e.target.value)}
                    className="mt-1.5 bg-[#18181F] border-amber-500/20"
                    placeholder="City"
                    data-testid="registration-city-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-300">Organization / Title</Label>
                <Input
                  value={form.organization}
                  onChange={(e) => set("organization", e.target.value)}
                  className="mt-1.5 bg-[#18181F] border-amber-500/20"
                  placeholder="Company / designation"
                  data-testid="registration-organization-input"
                />
              </div>

              <div>
                <Label className="text-zinc-300">Award Category *</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger
                    className="mt-1.5 bg-[#18181F] border-amber-500/20"
                    data-testid="registration-category-select"
                  >
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181F] border-amber-500/25 text-zinc-100">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.id} value={c.title} data-testid={`category-option-${c.id}`}>
                        {c.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4 p-4 rounded-xl bg-[#18181F] gold-border">
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={form.is_jfc}
                    onCheckedChange={(v) => set("is_jfc", !!v)}
                    data-testid="registration-jfc-member-checkbox"
                  />
                  <span className="text-sm text-zinc-200">
                    Nominated through JFC (registration fee waived)
                  </span>
                </label>

                {!form.is_jfc && (
                  <div>
                    <Label className="text-zinc-300 text-sm">Enrolled by (team member)</Label>
                    <Input
                      value={form.enroller_name}
                      onChange={(e) => set("enroller_name", e.target.value)}
                      className="mt-1.5 bg-[#09090B] border-amber-500/20"
                      placeholder="Team member name ($50 commission)"
                      data-testid="registration-enroller-input"
                    />
                  </div>
                )}

                <Counter
                  label="Accompanying members (1 free)"
                  value={form.accompanying_members}
                  onChange={(v) => set("accompanying_members", v)}
                  testid="registration-accompanying-count"
                />

                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={form.stay_tour}
                    onCheckedChange={(v) => set("stay_tour", !!v)}
                    data-testid="registration-stay-tour-checkbox"
                  />
                  <span className="text-sm text-zinc-200">Opt for Stay & Tour package</span>
                </label>

                {form.stay_tour && (
                  <Counter
                    label="People for Stay & Tour ($50 each)"
                    value={form.stay_tour_persons}
                    onChange={(v) => set("stay_tour_persons", v)}
                    testid="registration-stay-tour-persons"
                  />
                )}

                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={form.av_required}
                    onCheckedChange={(v) => set("av_required", !!v)}
                    data-testid="registration-av-checkbox"
                  />
                  <span className="text-sm text-zinc-200">
                    Need VV Entertainments to create AV (extra charge, quoted separately)
                  </span>
                </label>
              </div>

              <div>
                <Label className="text-zinc-300">Notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  className="mt-1.5 bg-[#18181F] border-amber-500/20"
                  placeholder="Anything we should know?"
                  data-testid="registration-notes-input"
                />
              </div>

              <div className="p-5 rounded-xl bg-gradient-to-br from-amber-500/10 to-transparent gold-border">
                <div className="text-xs font-mono-lux tracking-[0.2em] text-amber-400 uppercase mb-3">
                  Fee Breakdown
                </div>
                <div className="space-y-1.5 text-sm">
                  <Row label="Registration Fee" value={inr(fees.registration_fee)} />
                  {fees.extra_member_fee > 0 && (
                    <Row label="Extra Members" value={inr(fees.extra_member_fee)} />
                  )}
                  {fees.stay_tour_fee > 0 && (
                    <Row label="Stay & Tour" value={inr(fees.stay_tour_fee)} />
                  )}
                  <div className="border-t border-amber-500/20 my-2" />
                  <div className="flex justify-between items-center">
                    <span className="text-amber-100 font-600">Total Payable</span>
                    <span
                      className="font-mono-lux text-2xl gold-text font-700"
                      data-testid="registration-total-amount"
                    >
                      {inr(fees.total)}
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full gold-gradient-bg text-black font-700 py-3.5 rounded-full hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="registration-submit-button"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5" /> Submit Nomination
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between text-zinc-300">
    <span>{label}</span>
    <span className="font-mono-lux text-amber-100">{value}</span>
  </div>
);

export const PaymentProofDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg bg-[#121216] border-amber-500/25 text-zinc-100">
      <DialogHeader>
        <DialogTitle className="font-display gold-text text-xl">Submit Payment Proof</DialogTitle>
        <DialogDescription className="text-sm text-zinc-400">
          Enter the email and phone you registered with, then add your UPI transaction ID or screenshot.
        </DialogDescription>
      </DialogHeader>
      <PaymentProofForm />
    </DialogContent>
  </Dialog>
);

export const QrDialog = ({ open, onOpenChange }) => {
  const url = typeof window !== "undefined" ? window.location.origin : "";
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&bgcolor=18181F&color=D4AF37&data=${encodeURIComponent(
    url
  )}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-[#121216] border-amber-500/25 text-center text-zinc-100">
        <DialogHeader>
          <DialogTitle className="font-display gold-text text-xl">Scan to Register</DialogTitle>
          <DialogDescription className="text-sm text-zinc-400">
            Scan the QR code to open the nomination page on your phone.
          </DialogDescription>
        </DialogHeader>
        <div className="p-5 bg-[#18181F] rounded-2xl gold-border inline-block mx-auto" data-testid="qr-code-image">
          <img src={qr} alt="Registration QR" className="w-56 h-56" />
        </div>
        <p className="text-sm text-zinc-400 mt-2">
          Scan with your phone camera to open the nomination page.
        </p>
      </DialogContent>
    </Dialog>
  );
};
